import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/shared";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Plus, Scale } from "lucide-react";

const month = new Date().toISOString().slice(0, 7);
const monthRange = (value) => {
  const [year, monthIndex] = value.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year, monthIndex, 0)).getUTCDate();
  return {
    start: `${value}-01`,
    end: `${value}-${String(lastDay).padStart(2, "0")}`,
  };
};
const initialLeave = {
  leave_type: "annual",
  start_date: "",
  end_date: "",
  half_day: false,
  reason: "",
};
const initialType = {
  code: "",
  name: "",
  is_paid: true,
  accrual_per_month: 0,
  carry_forward_max: 0,
  max_balance: 0,
  approval_levels: ["manager"],
  allow_negative: false,
  status: "active",
};

export default function LeaveManagement() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [calendar, setCalendar] = useState({
    events: [],
    conflict_warnings: [],
  });
  const [employees, setEmployees] = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leave, setLeave] = useState(initialLeave);
  const [leaveType, setLeaveType] = useState(initialType);
  const [adjustment, setAdjustment] = useState({
    user_id: "",
    leave_type_code: "annual",
    days: "",
    reason: "",
  });
  const canManage = ["super_admin", "founder", "hr", "hr_admin"].includes(
    user?.role,
  );
  const { start, end } = monthRange(month);

  const load = useCallback(async () => {
    try {
      const [
        typeResponse,
        balanceResponse,
        leaveResponse,
        calendarResponse,
        employeeResponse,
      ] = await Promise.all([
        api.get("/hrms/leave-types"),
        api.get("/hrms/leave-balances"),
        api.get("/hrms/leaves"),
        api.get("/hrms/leave-calendar", { params: { start, end } }),
        api.get("/hrms/employees"),
      ]);
      setTypes(typeResponse.data.items || []);
      setBalances(balanceResponse.data.balances || []);
      setLedger(balanceResponse.data.ledger || []);
      setLeaves(leaveResponse.data.items || []);
      setCalendar(calendarResponse.data);
      setEmployees(employeeResponse.data.items || []);
    } catch (error) {
      toast.error(apiErr(error));
    }
  }, [end, start]);
  useEffect(() => {
    load();
  }, [load]);

  const post = async (path, payload, message) => {
    setSaving(true);
    try {
      await api.post(path, payload);
      toast.success(message);
      load();
      return true;
    } catch (error) {
      toast.error(apiErr(error));
      return false;
    } finally {
      setSaving(false);
    }
  };
  const submitLeave = async () => {
    if (await post("/hrms/leaves", leave, "Leave request submitted")) {
      setLeaveOpen(false);
      setLeave(initialLeave);
    }
  };
  const submitType = async () => {
    if (await post("/hrms/leave-types", leaveType, "Leave type created")) {
      setTypeOpen(false);
      setLeaveType(initialType);
    }
  };
  const submitAdjustment = async () => {
    if (
      await post(
        `/hrms/leave-balances/${adjustment.user_id}/adjust`,
        { ...adjustment, days: Number(adjustment.days) },
        "Leave balance adjusted",
      )
    ) {
      setAdjustOpen(false);
      setAdjustment({
        user_id: "",
        leave_type_code: "annual",
        days: "",
        reason: "",
      });
    }
  };
  const runAccrual = async () => {
    await post(
      "/hrms/leave-accruals/run",
      { period: month },
      "Monthly accrual completed",
    );
  };
  const runCarryForward = async () => {
    await post(
      "/hrms/leave-carry-forward/run",
      { year: Number(month.slice(0, 4)) },
      "Carry-forward completed",
    );
  };
  const decide = async (decision) => {
    if (
      await post(
        `/hrms/leaves/${decisionTarget.id}/decision`,
        decision,
        `Leave ${decision.decision}`,
      )
    )
      setDecisionTarget(null);
  };

  return (
    <div className="space-y-6" data-testid="leave-management-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/hrms"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="leave-management-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> People & HRMS
          </Link>
          <h1 className="mt-3 font-display text-3xl font-bold">
            Leave & balance management
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Self-service balances, controlled adjustments, multi-level
            approvals, and team availability.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setLeaveOpen(true)}
            data-testid="leave-management-request-button"
          >
            <CalendarDays className="mr-1.5 h-4 w-4" /> Request leave
          </Button>
          {canManage && (
            <>
              <Button
                variant="outline"
                onClick={() => setAdjustOpen(true)}
                data-testid="leave-management-adjust-button"
              >
                <Scale className="mr-1.5 h-4 w-4" /> Adjust balance
              </Button>
              <Button
                onClick={() => setTypeOpen(true)}
                data-testid="leave-management-type-button"
                className="bg-[#255058] text-white hover:bg-[#1c3d43]"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Leave type
              </Button>
              <Button
                variant="outline"
                onClick={runAccrual}
                disabled={saving}
                data-testid="leave-run-accrual-button"
              >
                Run accrual
              </Button>
              <Button
                variant="outline"
                onClick={runCarryForward}
                disabled={saving}
                data-testid="leave-run-carry-forward-button"
              >
                Carry forward
              </Button>
            </>
          )}
        </div>
      </div>
      <section
        className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
        data-testid="leave-balance-grid"
      >
        {balances.map((item) => (
          <Card
            key={item.id}
            className="p-5"
            data-testid={`leave-balance-${item.code}`}
          >
            <div className="flex justify-between gap-3">
              <div>
                <div className="font-display text-lg font-semibold">
                  {item.name}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {item.is_paid ? "Paid leave" : "Unpaid / LOP"}
                </div>
              </div>
              <div className="text-3xl font-bold text-[#255058]">
                {item.balance}
              </div>
            </div>
            <div className="mt-4 text-xs text-muted-foreground">
              Accrual {item.accrual_per_month}/month · Carry-forward{" "}
              {item.carry_forward_max}
            </div>
          </Card>
        ))}
      </section>
      <div className="grid gap-5 xl:grid-cols-[1.15fr_1fr]">
        <Card className="overflow-hidden" data-testid="leave-calendar-card">
          <div className="border-b px-5 py-4">
            <div className="font-display text-lg font-semibold">
              Team leave calendar
            </div>
            <div className="text-sm text-muted-foreground">
              {month} · managers see only their team.
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y">
            {calendar.events.map((item) => (
              <div
                key={item.id}
                className="flex justify-between gap-4 px-5 py-3 text-sm"
                data-testid={`leave-calendar-event-${item.id}`}
              >
                <div>
                  <span className="font-medium">{item.requester_name}</span> ·{" "}
                  {item.leave_type}
                </div>
                <div className="text-right">
                  <span>
                    {item.start_date} → {item.end_date}
                  </span>
                  <div className="mt-1">
                    <StatusBadge value={item.status} />
                  </div>
                </div>
              </div>
            ))}
            {!calendar.events.length && (
              <div className="px-5 py-8 text-sm text-muted-foreground">
                No leave planned in your visible scope.
              </div>
            )}
          </div>
        </Card>
        <Card className="p-5" data-testid="leave-conflict-card">
          <div className="font-display text-lg font-semibold">
            Conflict warnings
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Overlapping leave in the same team is highlighted before capacity is
            impacted.
          </div>
          <div className="mt-4 space-y-2">
            {calendar.conflict_warnings.map((item) => (
              <div
                key={item.key}
                className="rounded-md border-l-4 border-l-amber-500 bg-amber-50 px-3 py-2 text-sm"
                data-testid={`leave-conflict-${item.key}`}
              >
                {item.key.split(":")[1]} · {item.count} people away
              </div>
            ))}
            {!calendar.conflict_warnings.length && (
              <div className="text-sm text-muted-foreground">
                No team conflicts for this month.
              </div>
            )}
          </div>
        </Card>
      </div>
      <LeaveTable leaves={leaves} onReview={setDecisionTarget} />
      <Card className="overflow-hidden" data-testid="leave-ledger-card">
        <div className="border-b px-5 py-4 font-display text-lg font-semibold">
          My balance ledger
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-5 py-3">Date</th>
                <th>Leave type</th>
                <th>Entry</th>
                <th>Days</th>
                <th className="pr-5">Reason</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map((item) => (
                <tr
                  key={item.id}
                  className="border-b last:border-0"
                  data-testid={`leave-ledger-row-${item.id}`}
                >
                  <td className="px-5 py-3">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td>{item.leave_type_code}</td>
                  <td>{item.entry_type}</td>
                  <td
                    className={
                      item.days < 0 ? "text-rose-700" : "text-emerald-700"
                    }
                  >
                    {item.days > 0 ? "+" : ""}
                    {item.days}
                  </td>
                  <td className="pr-5">{item.reason || "—"}</td>
                </tr>
              ))}
              {!ledger.length && (
                <tr>
                  <td colSpan={5} className="px-5 py-6 text-muted-foreground">
                    No ledger activity yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <LeaveDialog
        open={leaveOpen}
        value={leave}
        types={types}
        saving={saving}
        onClose={() => setLeaveOpen(false)}
        onChange={setLeave}
        onSubmit={submitLeave}
      />
      <LeaveTypeDialog
        open={typeOpen}
        value={leaveType}
        saving={saving}
        onClose={() => setTypeOpen(false)}
        onChange={setLeaveType}
        onSubmit={submitType}
      />
      <AdjustmentDialog
        open={adjustOpen}
        value={adjustment}
        employees={employees}
        types={types}
        saving={saving}
        onClose={() => setAdjustOpen(false)}
        onChange={setAdjustment}
        onSubmit={submitAdjustment}
      />
      <DecisionDialog
        target={decisionTarget}
        saving={saving}
        onClose={() => setDecisionTarget(null)}
        onSubmit={decide}
      />
    </div>
  );
}

function LeaveTable({ leaves, onReview }) {
  return (
    <Card className="overflow-hidden" data-testid="leave-requests-board">
      <div className="border-b px-5 py-4 font-display text-lg font-semibold">
        Leave requests
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-5 py-3">Employee</th>
              <th>Dates</th>
              <th>Type</th>
              <th>Days</th>
              <th>Status</th>
              <th className="pr-5">Action</th>
            </tr>
          </thead>
          <tbody>
            {leaves.map((item) => (
              <tr
                key={item.id}
                className="border-b last:border-0"
                data-testid={`leave-request-row-${item.id}`}
              >
                <td className="px-5 py-3 font-medium">{item.requester_name}</td>
                <td>
                  {item.start_date} → {item.end_date}
                </td>
                <td>{item.leave_type}</td>
                <td>{item.leave_days ?? "—"}</td>
                <td>
                  <StatusBadge value={item.status} />
                </td>
                <td className="pr-5">
                  {["pending", "pending_hr"].includes(item.status) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReview(item)}
                      data-testid={`leave-review-${item.id}`}
                    >
                      Review
                    </Button>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {!leaves.length && (
              <tr>
                <td colSpan={6} className="px-5 py-6 text-muted-foreground">
                  No leave requests in your scope.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function LeaveDialog({
  onChange,
  onClose,
  onSubmit,
  open,
  saving,
  types,
  value,
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="leave-request-dialog">
        <DialogHeader>
          <DialogTitle>Request leave</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Leave type">
            <Select
              value={value.leave_type}
              onValueChange={(leave_type) => onChange({ ...value, leave_type })}
            >
              <SelectTrigger data-testid="leave-request-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {types.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Start date">
            <Input
              type="date"
              value={value.start_date}
              onChange={(event) =>
                onChange({ ...value, start_date: event.target.value })
              }
              data-testid="leave-request-start-input"
            />
          </Field>
          <Field label="End date">
            <Input
              type="date"
              value={value.end_date}
              onChange={(event) =>
                onChange({ ...value, end_date: event.target.value })
              }
              data-testid="leave-request-end-input"
            />
          </Field>
          <Field label="Reason">
            <Textarea
              value={value.reason}
              onChange={(event) =>
                onChange({ ...value, reason: event.target.value })
              }
              data-testid="leave-request-reason-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="leave-request-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              saving ||
              !value.start_date ||
              !value.end_date ||
              !value.reason.trim()
            }
            data-testid="leave-request-submit-button"
          >
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeaveTypeDialog({ onChange, onClose, onSubmit, open, saving, value }) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="leave-type-dialog">
        <DialogHeader>
          <DialogTitle>Configure leave type</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Code">
            <Input
              value={value.code}
              onChange={(event) =>
                onChange({ ...value, code: event.target.value })
              }
              data-testid="leave-type-code-input"
            />
          </Field>
          <Field label="Name">
            <Input
              value={value.name}
              onChange={(event) =>
                onChange({ ...value, name: event.target.value })
              }
              data-testid="leave-type-name-input"
            />
          </Field>
          <Field label="Monthly accrual">
            <Input
              type="number"
              value={value.accrual_per_month}
              onChange={(event) =>
                onChange({
                  ...value,
                  accrual_per_month: Number(event.target.value),
                })
              }
              data-testid="leave-type-accrual-input"
            />
          </Field>
          <Field label="Carry-forward max">
            <Input
              type="number"
              value={value.carry_forward_max}
              onChange={(event) =>
                onChange({
                  ...value,
                  carry_forward_max: Number(event.target.value),
                })
              }
              data-testid="leave-type-carry-input"
            />
          </Field>
          <Field label="Max balance">
            <Input
              type="number"
              value={value.max_balance}
              onChange={(event) =>
                onChange({ ...value, max_balance: Number(event.target.value) })
              }
              data-testid="leave-type-max-input"
            />
          </Field>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value.is_paid}
            onChange={(event) =>
              onChange({ ...value, is_paid: event.target.checked })
            }
            data-testid="leave-type-paid-checkbox"
          />{" "}
          Paid leave
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value.allow_negative}
            onChange={(event) =>
              onChange({ ...value, allow_negative: event.target.checked })
            }
            data-testid="leave-type-negative-checkbox"
          />{" "}
          Allow negative balance
        </label>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="leave-type-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || !value.code || !value.name}
            data-testid="leave-type-save-button"
          >
            Save leave type
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentDialog({
  employees,
  onChange,
  onClose,
  onSubmit,
  open,
  saving,
  types,
  value,
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="leave-adjustment-dialog">
        <DialogHeader>
          <DialogTitle>Adjust leave balance</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Employee">
            <Select
              value={value.user_id}
              onValueChange={(user_id) => onChange({ ...value, user_id })}
            >
              <SelectTrigger data-testid="leave-adjustment-employee-select">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {employees
                  .filter((item) => item.user_id)
                  .map((item) => (
                    <SelectItem key={item.user_id} value={item.user_id}>
                      {item.name || item.employee_id}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Leave type">
            <Select
              value={value.leave_type_code}
              onValueChange={(leave_type_code) =>
                onChange({ ...value, leave_type_code })
              }
            >
              <SelectTrigger data-testid="leave-adjustment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {types.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Days (+ credit / - debit)">
            <Input
              type="number"
              step="0.5"
              value={value.days}
              onChange={(event) =>
                onChange({ ...value, days: event.target.value })
              }
              data-testid="leave-adjustment-days-input"
            />
          </Field>
          <Field label="Reason">
            <Textarea
              value={value.reason}
              onChange={(event) =>
                onChange({ ...value, reason: event.target.value })
              }
              data-testid="leave-adjustment-reason-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="leave-adjustment-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              saving || !value.user_id || !value.days || !value.reason.trim()
            }
            data-testid="leave-adjustment-submit-button"
          >
            Apply adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ onClose, onSubmit, saving, target }) {
  const [form, setForm] = useState({
    decision: "approved",
    comment: "",
    reason: "",
  });
  useEffect(
    () => setForm({ decision: "approved", comment: "", reason: "" }),
    [target],
  );
  if (!target) return null;
  return (
    <Dialog open={Boolean(target)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="leave-decision-dialog">
        <DialogHeader>
          <DialogTitle>Review leave request</DialogTitle>
        </DialogHeader>
        <Field label="Decision">
          <Select
            value={form.decision}
            onValueChange={(decision) => setForm({ ...form, decision })}
          >
            <SelectTrigger data-testid="leave-decision-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="approved">Approve</SelectItem>
              <SelectItem value="rejected">Reject</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Comment">
          <Textarea
            value={form.comment}
            onChange={(event) =>
              setForm({ ...form, comment: event.target.value })
            }
            data-testid="leave-decision-comment-input"
          />
        </Field>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="leave-decision-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(form)}
            disabled={saving}
            data-testid="leave-decision-submit-button"
          >
            Save decision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function Field({ children, label }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
